import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { X, UploadSimple, Storefront } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useBodyScrollLock } from "@/lib/useBodyScrollLock";
import { sortHeights } from "@/lib/heights";
import { track } from "@/lib/track";
import CityInput from "@/components/CityInput";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MAX_NOTES = 500;
const MAX_IMAGE_BYTES = 5 * 1024 * 1024;

// "Share an offline spot" — a physical store where something actually fit.
//
// The size is the load-bearing field: a shop name without one is just a shop.
// Which sizes are offered comes from the tab this spot will land in (the
// category matching the sharer's gender + what they bought), so the options can
// never disagree with where the post ends up — the server derives that same
// tab independently when it saves.
const OfflineSpotComposer = ({
  open,
  initialKind = "clothing",
  user,
  categories: categoriesProp,
  onClose,
  onSuccess,
}) => {
  useBodyScrollLock(open);
  // Callers that already hold the category list pass it in; the rest (the share
  // picker, which doesn't) get a self-fetch, so this composer works anywhere
  // without threading the list through every parent.
  const [fetchedCategories, setFetchedCategories] = useState([]);
  const categories = categoriesProp?.length ? categoriesProp : fetchedCategories;
  useEffect(() => {
    if (!open || categoriesProp?.length) return;
    let cancelled = false;
    axios
      .get(`${API}/categories`)
      .then((r) => !cancelled && setFetchedCategories(r.data || []))
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open, categoriesProp]);
  // Clothes vs shoes lives here rather than in the outer kind picker: it decides
  // which sizes are offered AND which tab the spot lands in, so it belongs next
  // to the size buttons it controls.
  const [kind, setKind] = useState(initialKind);
  const [sizes, setSizes] = useState([]);
  const [storeName, setStoreName] = useState("");
  const [area, setArea] = useState("");
  const [city, setCity] = useState("");
  const [notes, setNotes] = useState("");
  const [image, setImage] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const fileRef = useRef(null);

  const isShoes = kind === "shoes";

  // The tab this will be filed under — mirrors _category_for_gender_kind on the
  // server. "women" contains "men", so women must be tested first.
  const targetCategory = (() => {
    const wantWomen = user?.gender === "female";
    return categories.find((c) => {
      if ((c.kind || "clothing") !== kind) return false;
      const n = (c.name || "").toLowerCase();
      const isWomen = n.includes("women") || n.includes("girls");
      const isMen = !isWomen && (n.includes("men") || n.includes("boys"));
      return wantWomen ? isWomen : isMen;
    });
  })();

  const sizeOptions = sortHeights(targetCategory?.heights || []);
  const sizeUnit = (targetCategory?.size_label || (isShoes ? "UK" : "ft")).trim();

  useEffect(() => {
    if (open) setKind(initialKind);
  }, [open, initialKind]);

  useEffect(() => {
    if (!open) return;
    track("form_start", { form_name: "offline_spot", form_id: "offline_spot_composer", kind });
    // Pre-fill with the size already on their profile — they're most likely
    // sharing a spot for their own size.
    const saved = isShoes ? user?.shoe_size : user?.height;
    setSizes(saved ? [String(saved)] : []);
    if (user?.city) setCity((c) => c || user.city);
  }, [open, kind, user, isShoes]);

  if (!open) return null;

  const toggleSize = (s) =>
    setSizes((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));

  // Heights and shoe sizes are different scales — carrying a selection across
  // the toggle would file "6.2" as a shoe size.
  const chooseKind = (next) => {
    if (next === kind) return;
    track("offline_spot_kind_select", { kind: next });
    setKind(next);
    const saved = next === "shoes" ? user?.shoe_size : user?.height;
    setSizes(saved ? [String(saved)] : []);
  };

  const clearImage = () => {
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(null);
    setImagePreview(null);
    if (fileRef.current) fileRef.current.value = "";
  };

  const handleImageChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (!f.type.startsWith("image/")) {
      toast.error("Please pick an image file");
      return;
    }
    if (f.size > MAX_IMAGE_BYTES) {
      toast.error("Image must be under 5MB");
      return;
    }
    if (imagePreview) URL.revokeObjectURL(imagePreview);
    setImage(f);
    setImagePreview(URL.createObjectURL(f));
  };

  const handleClose = () => {
    track("form_abandon", {
      form_name: "offline_spot",
      form_id: "offline_spot_composer",
      kind,
      had_content: !!(storeName || city || notes || sizes.length),
    });
    clearImage();
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (sizes.length === 0) {
      track("form_error", { form_name: "offline_spot", reason: "missing_size" });
      toast.error(isShoes ? "Pick the shoe size you bought" : "Pick the height it fit");
      return;
    }
    if (!storeName.trim()) {
      track("form_error", { form_name: "offline_spot", reason: "missing_store" });
      toast.error("Add the store name");
      return;
    }
    if (!city.trim()) {
      track("form_error", { form_name: "offline_spot", reason: "missing_city" });
      toast.error("Add the city");
      return;
    }

    track("form_submit", {
      form_name: "offline_spot",
      form_id: "offline_spot_composer",
      kind,
      size_count: sizes.length,
      has_image: !!image,
      has_notes: !!notes.trim(),
    });
    setSubmitting(true);
    try {
      const fd = new FormData();
      fd.append("kind", kind);
      fd.append("sizes", sizes.join(","));
      fd.append("store_name", storeName.trim());
      fd.append("city", city.trim());
      if (area.trim()) fd.append("area", area.trim());
      if (notes.trim()) fd.append("notes", notes.trim());
      if (image) fd.append("image", image);

      await axios.post(`${API}/offline-spots`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
        withCredentials: true,
      });
      track("share", {
        method: "offline_spot_composer",
        content_type: "offline_spot",
        kind,
        has_image: !!image,
      });
      toast.success("Spot shared - thanks for putting it on the map.");
      clearImage();
      onClose();
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("Offline spot submit failed:", err);
      track("share_failed", {
        method: "offline_spot_composer",
        content_type: "offline_spot",
        error_status: err?.response?.status,
      });
      toast.error(err.response?.data?.detail || "Could not share this spot");
    } finally {
      setSubmitting(false);
    }
  };

  const labelCls =
    "block text-xs uppercase tracking-[0.2em] mb-2 text-[#6B6A68]";
  const labelStyle = { fontFamily: "IBM Plex Mono, monospace" };
  const inputCls =
    "w-full px-4 py-3 bg-white border border-[#E4E1D9] rounded-md text-sm focus:outline-none focus:ring-1 focus:ring-[#2D302A]";

  return (
    <div
      className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm p-4 flex items-center justify-center"
      data-testid="offline-spot-overlay"
    >
      <div
        className="bg-white w-full max-w-lg rounded-md border border-[#E4E1D9] p-6 md:p-8 max-h-[90vh] overflow-y-auto"
        data-lenis-prevent
        data-testid="offline-spot-dialog"
      >
        <div className="flex justify-between items-start mb-2">
          <h2
            className="text-3xl tracking-tighter"
            style={{ fontFamily: "Cormorant Garamond, serif" }}
          >
            Share an offline spot
          </h2>
          <button
            onClick={handleClose}
            className="text-[#6B6A68] hover:text-[#1A1A1A] transition-colors"
            data-testid="offline-spot-close"
            aria-label="Close"
          >
            <X size={24} />
          </button>
        </div>
        <p className="text-sm text-[#6B6A68] mb-6">
          {isShoes
            ? "A real store where you found shoes in your size."
            : "A real store where you found something that actually fit."}
          {targetCategory ? ` Goes to ${targetCategory.name}.` : ""}
        </p>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* What kind of spot — drives both the size scale and the target tab */}
          <div>
            <label className={labelCls} style={labelStyle}>What did you buy?</label>
            <div className="grid grid-cols-2 gap-2" data-testid="offline-spot-kind">
              {[
                { value: "clothing", label: "Clothes" },
                { value: "shoes", label: "Shoes" },
              ].map((o) => (
                <button
                  key={o.value}
                  type="button"
                  onClick={() => chooseKind(o.value)}
                  className={`px-4 py-3 rounded-md border text-xs uppercase tracking-[0.15em] transition-colors ${
                    kind === o.value
                      ? "bg-[#2D302A] text-white border-[#2D302A]"
                      : "bg-white text-[#2D302A] border-[#E4E1D9] hover:border-[#C9C5BA]"
                  }`}
                  style={labelStyle}
                  aria-pressed={kind === o.value}
                  data-testid={`offline-spot-kind-${o.value}`}
                >
                  {o.label}
                </button>
              ))}
            </div>
          </div>

          {/* Size — heights for clothing, shoe sizes for shoes */}
          <div>
            <label className={labelCls} style={labelStyle}>
              {isShoes ? `Size you bought (${sizeUnit})` : `Height it fit (${sizeUnit})`}
            </label>
            {sizeOptions.length === 0 ? (
              <p className="text-sm text-[#9A9A98]">
                No sizes are set up for this section yet.
              </p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="offline-spot-sizes">
                {sizeOptions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => toggleSize(s)}
                    className={`px-3 py-2 rounded-md border text-xs transition-colors ${
                      sizes.includes(s)
                        ? "bg-[#2D302A] text-white border-[#2D302A]"
                        : "bg-white text-[#2D302A] border-[#E4E1D9] hover:border-[#C9C5BA]"
                    }`}
                    style={labelStyle}
                    aria-pressed={sizes.includes(s)}
                  >
                    {isShoes ? `${sizeUnit} ${s}` : `${s} ${sizeUnit}`}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div>
            <label className={labelCls} style={labelStyle}>Store</label>
            <input
              value={storeName}
              onChange={(e) => setStoreName(e.target.value)}
              placeholder="Nike"
              className={inputCls}
              maxLength={80}
              data-testid="offline-spot-store"
            />
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label className={labelCls} style={labelStyle}>Mall / area</label>
              <input
                value={area}
                onChange={(e) => setArea(e.target.value)}
                placeholder="Inorbit Mall, Malad"
                className={inputCls}
                maxLength={120}
                data-testid="offline-spot-area"
              />
            </div>
            <div>
              <label className={labelCls} style={labelStyle}>City</label>
              <CityInput
                value={city}
                onChange={setCity}
                className={inputCls}
                testId="offline-spot-city"
              />
            </div>
          </div>

          <div>
            <label className={labelCls} style={labelStyle}>What did you get there?</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value.slice(0, MAX_NOTES))}
              rows={3}
              placeholder={
                isShoes
                  ? "Got UK 12 running shoes - they stock up to 13 here."
                  : "34x34 straight jeans that actually reach the ankle."
              }
              className={`${inputCls} resize-none`}
              data-testid="offline-spot-notes"
            />
            <p className="mt-1 text-[11px] text-[#9A9A98]" style={labelStyle}>
              {notes.length}/{MAX_NOTES}
            </p>
          </div>

          {/* Optional photo */}
          <div>
            <label className={labelCls} style={labelStyle}>Photo (optional)</label>
            {imagePreview ? (
              <div className="relative inline-block">
                <img
                  src={imagePreview}
                  alt="Preview"
                  className="h-32 rounded-md border border-[#E4E1D9] object-cover"
                />
                <button
                  type="button"
                  onClick={clearImage}
                  className="absolute -top-2 -right-2 bg-white border border-[#E4E1D9] rounded-full p-1 shadow"
                  aria-label="Remove photo"
                >
                  <X size={14} />
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                className="w-full flex items-center justify-center gap-2 px-4 py-6 border border-dashed border-[#E4E1D9] rounded-md text-sm text-[#6B6A68] hover:border-[#C9C5BA] transition-colors"
                data-testid="offline-spot-photo"
              >
                <UploadSimple size={18} /> Add a photo
              </button>
            )}
            <input
              ref={fileRef}
              type="file"
              accept="image/*"
              onChange={handleImageChange}
              className="hidden"
            />
          </div>

          <button
            type="submit"
            disabled={submitting}
            className="w-full flex items-center justify-center gap-2 bg-[#2D302A] text-white px-6 py-4 rounded-md hover:bg-[#1A1C18] transition-colors disabled:opacity-50"
            style={labelStyle}
            data-testid="offline-spot-submit"
          >
            <Storefront size={18} weight="fill" />
            <span className="text-xs uppercase tracking-[0.2em]">
              {submitting ? "Sharing…" : "Share this spot"}
            </span>
          </button>
        </form>
      </div>
    </div>
  );
};

export default OfflineSpotComposer;
